"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { Search, X } from "lucide-react";
import Link from "next/link";
import Fuse from "fuse.js";
import type { BlogPostMeta } from "@/lib/blog";
import { cn } from "@v1/ui/utils";

interface BlogSearchProps {
  posts: BlogPostMeta[];
  /**
   * Visual variant. Use `dark` when the search sits on the cinematic
   * dark hero (glass field, white text). The dropdown of results
   * always uses the light/default treatment because it overlays the
   * page surface below the hero.
   */
  variant?: "light" | "dark";
}

export function BlogSearch({ posts, variant = "light" }: BlogSearchProps) {
  const isDark = variant === "dark";
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fuse = useMemo(() => new Fuse(posts, {
      keys: [
        { name: "title", weight: 0.4 },
        { name: "excerpt", weight: 0.3 },
        { name: "category", weight: 0.2 },
        { name: "tags", weight: 0.1 },
      ],
      threshold: 0.3,
      includeScore: true,
    }), [posts]);
  const results = useMemo(
    () => query ? fuse.search(query).map((result) => result.item) : [],
    [fuse, query],
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
      if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative max-w-md">
      <div className="relative">
        <Search
          className={cn(
            "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4",
            isDark ? "text-white/50" : "text-muted-foreground",
          )}
        />
        <input
          ref={inputRef}
          type="text"
          aria-label="Search articles"
          placeholder="Search articles..."
          value={query}
          onChange={(e) => {
            const nextQuery = e.target.value;
            setQuery(nextQuery);
            setIsOpen(Boolean(nextQuery));
          }}
          onFocus={() => query.length > 0 && setIsOpen(true)}
          className={cn(
            "w-full h-10 pl-10 pr-12 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-all",
            isDark
              ? "border-white/15 bg-white/5 text-white placeholder:text-white/50 backdrop-blur-sm focus:ring-white/20 focus:border-white/30"
              : "border-border/60 bg-background placeholder:text-muted-foreground focus:ring-primary/20 focus:border-primary/50",
          )}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                setIsOpen(false);
                inputRef.current?.focus();
              }}
              className={cn(
                "p-1 rounded",
                isDark ? "hover:bg-white/10" : "hover:bg-muted",
              )}
            >
              <X
                className={cn(
                  "h-3 w-3",
                  isDark ? "text-white/60" : "text-muted-foreground",
                )}
              />
            </button>
          )}
          <kbd
            className={cn(
              "hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded border",
              isDark
                ? "text-white/60 bg-white/10 border-white/15"
                : "text-muted-foreground bg-muted/50 border-border/40",
            )}
          >
            <span className="text-[11px]">⌘</span>K
          </kbd>
        </div>
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border/60 rounded-lg shadow-lg overflow-hidden z-50">
          <ul className="py-2 max-h-80 overflow-y-auto">
            {results.map((post) => (
              <li key={post.slug}>
                <Link
                  href={`/blog/${post.slug}`}
                  onClick={() => {
                    setIsOpen(false);
                    setQuery("");
                  }}
                  className="block px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {post.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {post.excerpt}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/50 px-2 py-0.5 rounded whitespace-nowrap">
                      {post.category}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isOpen && query.length > 0 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border/60 rounded-lg shadow-lg overflow-hidden z-50">
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              No articles found for "{query}"
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
